import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../../PageHeader/PageHeader'
import AppButton from '../../../components/AppButton/AppButton';
import AppInput from '../../../components/AppInput/AppInput';
import { useDispatch, useSelector } from 'react-redux';
import MultiSelect from '../../../components/AppSelect/MultiSelect';
import { addSalonChild, updateSalonChild, searchSalonParent, searchUser, searchProduct } from '../../../constant/Constant';
import { HitApi } from '../../../Api/ApiHit';
import { getSelectedUnit } from '../../../storage/Storage';
import FileuploadComp from '../../../components/SingleFileUpload/FileuploadComp';
import { setApiErrorJson, setApiJson } from '../../../redux/Actions/ApiAction';
import toast from 'react-hot-toast';
import AppSelect from '../../../components/AppSelect/AppSelect';
import { Trash2, Plus } from 'lucide-react';
import { unitOptions } from '../../../constant/Options';
import SalonServiceSteps from './SalonServiceSteps/SalonServiceSteps';

function AddSalonChild({ editMode = false }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const [staffOptions, setStaffOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [parent, setParent] = useState(null);
  const [products, setProducts] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isDataPopulated, setIsDataPopulated] = useState(false);
  const [steps, setSteps] = useState([]);
  const [stepError, setStepError] = useState('');
  const [miscItems, setMiscItems] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: urlId } = useParams();

  const parentUrlId = editMode
    ? (typeof ApiReducer?.apiJson?.parentId === 'object'
        ? ApiReducer?.apiJson?.parentId?._id
        : ApiReducer?.apiJson?.parentId)
    : urlId;

  // Reset state when component mounts or mode changes
  useEffect(() => {
    if (!editMode) {
      // Clear all state for add mode
      setIsDataPopulated(false);
      setProducts([]);
      setSteps([]);
      setMiscItems([]);
      setParent(null);
      // Clear apiJson for fresh form
      dispatch(setApiJson({}));
      dispatch(setApiErrorJson({}));
    }

    // Cleanup on unmount
    return () => {
      dispatch(setApiJson({}));
      dispatch(setApiErrorJson({}));
    };
  }, [editMode, dispatch]);

  // Miscellaneous items functions
  const addMiscItem = () => {
    const newItem = {
      id: Date.now().toString(),
      name: '',
      price: ''
    };
    setMiscItems([...miscItems, newItem]);
  };

  const removeMiscItem = (itemId) => {
    setMiscItems(miscItems.filter(item => item.id !== itemId));
  };

  const updateMiscItem = (itemId, field, value) => {
    setMiscItems(miscItems.map(item => {
      if (item.id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  // Calculate total misc items cost
  const getTotalMiscCost = () => {
    return miscItems.reduce((total, item) => {
      return total + (parseFloat(item.price) || 0);
    }, 0);
  };

  // Calculate total service time in minutes from all steps
  const calculateTotalServiceTime = () => {
    const totalMinutes = steps.reduce((total, step) => {
      const stepTime = parseInt(step.service_time) || 0;
      return total + stepTime;
    }, 0);
    return totalMinutes;
  };

  // Validate existing steps before adding a new one
  const validateStepsBeforeAdd = () => {
    setStepError('');

    if (steps.length === 0) {
      return true;
    }

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];

      if (!step.stepName || step.stepName.trim() === '') {
        setStepError(`Step ${i + 1}: Step Name is required`);
        return false;
      }

      if (!step.service_time || step.service_time.toString().trim() === '') {
        setStepError(`Step ${i + 1}: Service Time is required`);
        return false;
      }

      if (isNaN(parseInt(step.service_time)) || parseInt(step.service_time) <= 0) {
        setStepError(`Step ${i + 1}: Service Time must be a valid number greater than 0`);
        return false;
      }
    }

    return true;
  };

  const addStep = () => {
    if (!validateStepsBeforeAdd()) {
      return;
    }

    const newStep = {
      id: Date.now().toString(),
      step: steps.length + 1,
      stepName: "",
      service_time: ""
    };
    setSteps([...steps, newStep]);
    setStepError('');
  };

  const removeStep = (stepId) => {
    const updated = steps.filter((step) => step.id !== stepId);
    const reNumbered = updated.map((step, index) => ({
      ...step,
      step: index + 1
    }));
    setSteps(reNumbered);
    setStepError('');
  };

  const updateStep = (stepId, field, value) => {
    const updated = steps.map((step) =>
      step.id === stepId ? { ...step, [field]: value } : step
    );
    setSteps(updated);

    if (stepError && (field === 'stepName' || field === 'service_time')) {
      setStepError('');
    }
  };

  useEffect(() => {
    if (parentUrlId) {
      getParent();
    }
    fetchStaff();
    fetchProducts();
  }, [parentUrlId]);

  useEffect(() => {
    if (editMode && ApiReducer?.apiJson && Object.keys(ApiReducer.apiJson).length > 0 && !isDataPopulated) {
      const timer = setTimeout(() => {
        populateEditData();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [editMode, ApiReducer?.apiJson, isDataPopulated]);

  const populateEditData = () => {
    if (isDataPopulated) return;

    const editData = ApiReducer?.apiJson;
    if (!editData || !editData._id) return;

    setIsDataPopulated(true);

    // Set parent data
    if (editData.parentId && typeof editData.parentId === 'object') {
      setParent(editData.parentId);
    }

    // Prepare updated data for redux
    let updatedData = { ...editData };

    // Convert staffIds from objects to IDs
    if (editData.staffIds && editData.staffIds.length > 0) {
      updatedData.staffIds = editData.staffIds
        .filter(staff => staff) // Filter out null/undefined
        .map(staff =>
          typeof staff === 'object' && staff?._id ? staff._id : staff
        )
        .filter(id => id); // Filter out empty values
    }

    // Convert parentId from object to ID
    if (editData.parentId && typeof editData.parentId === 'object' && editData.parentId?._id) {
      updatedData.parentId = editData.parentId._id;
    }

    // Convert unitIds from object to ID
    if (editData.unitIds && typeof editData.unitIds === 'object' && editData.unitIds?._id) {
      updatedData.unitIds = editData.unitIds._id;
    }

    // Dispatch updated data
    dispatch(setApiJson(updatedData));

    // Populate products
    if (editData.products && editData.products.length > 0) {
      const populatedProducts = editData.products
        .filter(product => product && product.product) // Filter out null/undefined products
        .map((product, index) => ({
          id: `product_${index}_${Date.now()}_${Math.random()}`,
          product: typeof product.product === 'object' && product.product?._id
            ? product.product._id
            : product.product || '',
          quantity: product.quantity || '',
          unit: product.unit || ''
        }));
      setProducts(populatedProducts);
    } else {
      setProducts([]);
    }

    // Populate steps
    if (editData.steps && editData.steps.length > 0) {
      const populatedSteps = editData.steps
        .filter(step => step) // Filter out null/undefined steps
        .map((step, index) => ({
          id: `step_${index}_${Date.now()}_${Math.random()}`,
          step: step?.step || index + 1,
          stepName: step?.stepName || '',
          service_time: step?.service_time || ''
        }));
      setSteps(populatedSteps);
    } else {
      setSteps([]);
    }

    // Populate misc items
    if (editData.miscItems && editData.miscItems.length > 0) {
      const populatedMiscItems = editData.miscItems
        .filter(item => item) // Filter out null/undefined items
        .map((item, index) => ({
          id: `misc_${index}_${Date.now()}_${Math.random()}`,
          name: item?.name || '',
          price: item?.price || ''
        }));
      setMiscItems(populatedMiscItems);
    } else {
      setMiscItems([]);
    }
  };

  const getParent = () => {
    setIsLoadingData(true);
    let json = {
      page: 1,
      limit: 1,
      search: {
        _id: parentUrlId,
        unitIds: getSelectedUnit()?._id
      }
    }
    HitApi(json, searchSalonParent).then((res) => {
      if (res?.data?.docs?.[0]) {
        setParent(res.data?.docs?.[0]);
      } else {
        toast.error('Salon parent not found');
        navigate('/salonServices');
      }
    }).catch((error) => {
      console.error("Error fetching parent:", error);
      toast.error('Error loading salon parent. Please try again.');
      navigate('/salonServices');
    }).finally(() => {
      setIsLoadingData(false);
    });
  }

  const fetchStaff = () => {
    var json = {
      page: 1,
      limit: 100,
      search: {
        userType: "Staff",
        unitIds: getSelectedUnit()?._id
      }
    };

    HitApi(json, searchUser).then((res) => {
      if (res?.statusCode === 200) {
        const options = res.data.docs?.map(staff => ({
          value: staff._id,
          label: staff.name,
        }));
        setStaffOptions(options);
      }
    }).catch((error) => {
      console.error("Error fetching staff:", error);
    });
  };

  const fetchProducts = () => {
    var json = {
      page: 1,
      limit: 1000,
      search: {
        unitIds: getSelectedUnit()?._id
      }
    };

    HitApi(json, searchProduct).then((res) => {
      if (res?.statusCode === 200) {
        console.log("Products API Response:", res.data.docs); // Debug log
        const options = res.data.docs?.map(product => ({
          value: product._id,
          label: product.productName,
          price: parseFloat(product.costPrice) || parseFloat(product.sellPrice) || parseFloat(product.price) || 0,
          unit: product.unit || '',
          netQuantity: parseFloat(product.netQuantity) || 1,
        }));
        console.log("Product Options:", options); // Debug log
        setProductOptions(options);
      }
    }).catch((error) => {
      console.error("Error fetching products:", error);
    });
  };

  // Get product details by ID
  const getProductDetails = (productId) => {
    return productOptions.find(p => p.value === productId);
  };

  // Get price per unit (e.g., price per ml)
  const getPricePerUnit = (productId) => {
    const product = getProductDetails(productId);
    if (product && product.price && product.netQuantity) {
      return product.price / product.netQuantity;
    }
    return product?.price || 0;
  };

  // Calculate cost for a product row
  const calculateProductCost = (productId, quantity) => {
    const product = getProductDetails(productId);
    if (product && quantity) {
      const pricePerUnit = getPricePerUnit(productId);
      return pricePerUnit * parseFloat(quantity);
    }
    return 0;
  };

  // Calculate total cost of all products
  const getTotalProductCost = () => {
    return products.reduce((total, p) => {
      return total + calculateProductCost(p.product, p.quantity);
    }, 0);
  };

  // Get service margin (subtracting both products and misc items)
  const getServiceMargin = () => {
    const servicePrice = parseFloat(ApiReducer?.apiJson?.price) || 0;
    const totalProductCost = getTotalProductCost();
    const totalMiscCost = getTotalMiscCost();
    return servicePrice - totalProductCost - totalMiscCost;
  };

  const addProduct = () => {
    const newProduct = {
      id: Date.now().toString(),
      product: '',
      quantity: '',
      unit: ''
    };
    setProducts([...products, newProduct]);
  };

  const removeProduct = (productId) => {
    setProducts(products.filter(p => p.id !== productId));
  };

  const updateProduct = (productId, field, value) => {
    setProducts(products.map(p => {
      if (p.id === productId) {
        const updated = { ...p, [field]: value };
        // Auto-fill unit when product is selected
        if (field === 'product') {
          const productDetails = getProductDetails(value);
          if (productDetails?.unit) {
            updated.unit = productDetails.unit;
          }
        }
        return updated;
      }
      return p;
    }));
  };

  const validateForm = (data) => {
    const errors = {};
    if (!data.name || data.name.trim() === '') {
      errors.name = 'Service name is required';
    }
    if (!data.price || data.price <= 0) {
      errors.price = 'Price is required';
    }
    return errors;
  };

  const handleSubmit = () => {
    setIsLoading(true);

    // Validate steps before submission
    if (steps.length > 0 && !validateStepsBeforeAdd()) {
      setIsLoading(false);
      return;
    }

    const totalServiceTime = calculateTotalServiceTime();

    // Clean products data
    const cleanedProducts = products.map(p => ({
      product: p.product,
      quantity: parseFloat(p.quantity) || 0,
      unit: p.unit
    })).filter(p => p.product);

    // Clean steps data
    const cleanedSteps = steps.map((step, index) => ({
      stepName: step.stepName,
      step: index + 1,
      service_time: step.service_time
    })).filter(step => step.stepName);

    // Clean misc items data
    const cleanedMiscItems = miscItems.map(item => ({
      name: item.name,
      price: parseFloat(item.price) || 0
    })).filter(item => item.name);

    // Calculate service margin
    const servicePrice = parseFloat(ApiReducer?.apiJson?.price) || 0;
    const totalProductCost = getTotalProductCost();
    const totalMiscCost = getTotalMiscCost();
    const calculatedMargin = servicePrice - totalProductCost - totalMiscCost;

    // Build payload
    const payload = {
      name: ApiReducer?.apiJson?.name,
      price: parseFloat(ApiReducer?.apiJson?.price) || 0,
      member_price: parseFloat(ApiReducer?.apiJson?.member_price) || 0,
      incentive: parseFloat(ApiReducer?.apiJson?.incentive) || 0,
      gender: ApiReducer?.apiJson?.gender || '',
      img: ApiReducer?.apiJson?.img || '',
      childDesc: ApiReducer?.apiJson?.childDesc || '',
      staffIds: ApiReducer?.apiJson?.staffIds || [],
      parentId: parentUrlId,
      unitIds: getSelectedUnit()?._id,
      products: cleanedProducts,
      steps: cleanedSteps,
      miscItems: cleanedMiscItems,
      service_time: steps.length > 0 ? String(totalServiceTime) : String(ApiReducer?.apiJson?.service_time || 0),
      serviceMargin: calculatedMargin
    };

    // Validate form
    const errors = validateForm(payload);
    dispatch(setApiErrorJson(errors));

    if (Object.keys(errors).length === 0) {
      if (editMode) {
        // For update, add _id from URL
        const updatePayload = {
          ...payload,
          _id: urlId
        };

        HitApi(updatePayload, updateSalonChild).then((res) => {
          if (res?.statusCode === 200) {
            toast.success('Salon service updated successfully!');
            dispatch(setApiJson({}));
            navigate('/salonServices');
          } else {
            toast.error(res?.message || 'Failed to update salon service');
          }
        }).catch((error) => {
          console.error('Update error:', error);
          toast.error('Error updating salon service. Please try again.');
        }).finally(() => {
          setIsLoading(false);
        });
      } else {
        // For add, no _id needed
        HitApi(payload, addSalonChild).then((res) => {
          if (res?.statusCode === 201 || res?.success) {
            toast.success('Salon service created successfully!');
            dispatch(setApiJson({}));
            navigate('/salonServices');
          } else {
            toast.error(res?.message || 'Failed to create salon service');
          }
        }).catch((error) => {
          console.error('Add error:', error);
          toast.error('Error creating salon service. Please try again.');
        }).finally(() => {
          setIsLoading(false);
        });
      }
    } else {
      setIsLoading(false);
      toast.error('Please fill all required fields');
    }
  };

  const hasError = (fieldName) => {
    return ApiReducer.apiJsonError && ApiReducer.apiJsonError[fieldName];
  };

  const getErrorMsg = (fieldName) => {
    return ApiReducer.apiJsonError ? ApiReducer.apiJsonError[fieldName] : "";
  };

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' }
  ];

  if (isLoadingData) {
    return (
      <div className='p-5'>
        <PageHeader
          title={editMode ? 'Update Salon Service' : 'Add New Salon Service'}
          description={editMode ? 'Update salon service information' : 'Create a new salon service'}
        />
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading parent information...</div>
        </div>
      </div>
    );
  }

  return (
    <div className='p-5'>
      <PageHeader
        title={editMode ? 'Update Salon Service' : 'Add New Salon Service'}
        description={editMode ? 'Update salon service information' : 'Create a new salon service'}
      />
      {parent ? (
        <>
          <div className="bg-white p-8 border rounded-xl mb-6">
            <div className="text-lg font-medium mb-5 flex justify-between items-center">
              <div className='font-bold'>{`Group : ${parent?.name}`}</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <AppInput
                title="Service Name"
                placeholder="Enter service name"
                name="name"
                important={true}
                error={hasError('name')}
                errormsg={getErrorMsg('name')}
                disabled={isLoading}
                str
              />

              <AppInput
                title="Regular Price"
                placeholder="Enter price"
                name="price"
                type='number'
                error={hasError('price')}
                errormsg={getErrorMsg('price')}
                important={true}
                disabled={isLoading}
                num
              />

              <AppInput
                title="Member Price"
                placeholder="Enter member price"
                name="member_price"
                type='number'
                disabled={isLoading}
                num
              />

              <div>
                {steps.length > 0 ? (
                  <>
                    <AppInput
                      title="Total Service Time (minutes)"
                      placeholder="Auto-calculated from steps"
                      name="service_time"
                      value={calculateTotalServiceTime()}
                      disabled={true}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Auto-calculated from all steps
                    </p>
                  </>
                ) : (
                  <AppInput
                    title="Service Time (minutes)"
                    placeholder="Enter service time"
                    name="service_time"
                    type="number"
                    disabled={isLoading}
                    num
                  />
                )}
              </div>

              <AppInput
                title="Incentive (%)"
                placeholder="Enter incentive"
                name="incentive"
                type='number'
                disabled={isLoading}
                num
              />

              <AppSelect
                title="Gender"
                name="gender"
                options={genderOptions}
                placeholder="Select gender"
                disabled={isLoading}
              />

              <MultiSelect
                title="Assign Staff"
                name="staffIds"
                options={staffOptions}
                placeholder="Select staff members"
                disabled={isLoading}
              />
            </div>

            <div className='mb-5'>
              <FileuploadComp
                title="Image URL"
                name="img"
                allowed={["img"]}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Products Section */}
          <div className="bg-white p-8 border rounded-xl mb-6">
            <div className="flex justify-between items-center mb-5">
              <div className="text-lg font-medium">Products Used</div>
              <button
                onClick={addProduct}
                className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg"
                disabled={isLoading}
              >
                <Plus size={16} />
                Add Product
              </button>
            </div>

            {products.length > 0 ? (
              <div className="space-y-4">
                {products.map((product) => (
                  <div key={product.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                        <div
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 h-11 cursor-pointer bg-white flex items-center justify-between"
                          onClick={() => {
                            setOpenDropdownId(openDropdownId === product.id ? null : product.id);
                            setProductSearchTerm('');
                          }}
                        >
                          <span className={product.product ? 'text-gray-900' : 'text-gray-400'}>
                            {productOptions.find(opt => opt.value === product.product)?.label || 'Select Product'}
                          </span>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${openDropdownId === product.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                        {openDropdownId === product.id && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-auto">
                            <div className="p-2 sticky top-0 bg-white border-b">
                              <input
                                type="text"
                                placeholder="Search products..."
                                value={productSearchTerm}
                                onChange={(e) => setProductSearchTerm(e.target.value)}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            {productOptions
                              .filter(opt => opt.label.toLowerCase().includes(productSearchTerm.toLowerCase()))
                              .map(opt => (
                                <div
                                  key={opt.value}
                                  className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${product.product === opt.value ? 'bg-blue-50 text-blue-600' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateProduct(product.id, 'product', opt.value);
                                    setOpenDropdownId(null);
                                    setProductSearchTerm('');
                                  }}
                                >
                                  <div className="flex justify-between items-center">
                                    <span>{opt.label}</span>
                                    <span className="text-sm text-gray-500">
                                      ₹{opt.price?.toFixed(2) || '0.00'}/{opt.netQuantity || 1}{opt.unit || ''}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            {productOptions.filter(opt => opt.label.toLowerCase().includes(productSearchTerm.toLowerCase())).length === 0 && (
                              <div className="px-3 py-2 text-gray-500 text-center">No products found</div>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                        <input
                          type="number"
                          value={product.quantity}
                          onChange={(e) => updateProduct(product.id, 'quantity', e.target.value)}
                          placeholder="Enter quantity"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          disabled={isLoading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                        <select
                          value={product.unit}
                          onChange={(e) => updateProduct(product.id, 'unit', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          disabled={isLoading}
                        >
                          <option value="">Select Unit</option>
                          {unitOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Price/Unit</label>
                        <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-100 text-gray-700">
                          ₹{getPricePerUnit(product.product)?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Total</label>
                        <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-100 text-gray-700 font-medium">
                          ₹{calculateProductCost(product.product, product.quantity).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeProduct(product.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      disabled={isLoading}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No products added. Click "Add Product" to add products used in this service.
              </div>
            )}

          </div>

          {/* Miscellaneous Items Section */}
          <div className="bg-white p-8 border rounded-xl mb-6">
            <div className="flex justify-between items-center mb-5">
              <div>
                <div className="text-lg font-medium">Miscellaneous Items</div>
                <p className="text-sm text-gray-500">Add consumables like towel, tissue, etc.</p>
              </div>
              <button
                onClick={addMiscItem}
                className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg"
                disabled={isLoading}
              >
                <Plus size={16} />
                Add Item
              </button>
            </div>

            {miscItems.length > 0 ? (
              <div className="space-y-4">
                {miscItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Item Name</label>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateMiscItem(item.id, 'name', e.target.value)}
                          placeholder="Enter item name (e.g., Towel, Tissue)"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          disabled={isLoading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
                        <input
                          type="number"
                          value={item.price}
                          onChange={(e) => updateMiscItem(item.id, 'price', e.target.value)}
                          placeholder="Enter price"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => removeMiscItem(item.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      disabled={isLoading}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                ))}

                {/* Misc Items Total */}
                <div className="flex justify-end pt-4 border-t">
                  <div className="text-gray-600">
                    <span>Total Misc Cost: </span>
                    <span className="font-medium text-red-500">₹{getTotalMiscCost().toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No miscellaneous items added. Click "Add Item" to add consumables.
              </div>
            )}
          </div>

          {/* Service Steps Section */}
          <SalonServiceSteps
            steps={steps}
            isLoading={isLoading}
            addStep={addStep}
            removeStep={removeStep}
            updateStep={updateStep}
            stepError={stepError}
          />

          {/* Total Service Time Display */}
          {steps.length > 0 && (
            <div className="bg-white p-4 border rounded-xl mt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium">Total Service Time:</span>
                <span className="text-xl font-bold text-blue-600">
                  {calculateTotalServiceTime()} minutes
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Auto-calculated from all step service times
              </p>
            </div>
          )}

          {/* Cost Summary - Compact */}
          {(products.length > 0 || miscItems.length > 0) && (
            <div className="bg-gray-50 p-4 border rounded-xl mt-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">Price:</span>
                    <span className="font-medium">₹{(parseFloat(ApiReducer?.apiJson?.price) || 0).toFixed(0)}</span>
                  </div>
                  {products.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Products:</span>
                      <span className="font-medium text-red-500">-₹{getTotalProductCost().toFixed(0)}</span>
                    </div>
                  )}
                  {miscItems.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Misc:</span>
                      <span className="font-medium text-red-500">-₹{getTotalMiscCost().toFixed(0)}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className={`text-lg font-bold ${getServiceMargin() >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    Margin: ₹{getServiceMargin().toFixed(0)}
                    {ApiReducer?.apiJson?.price > 0 && (
                      <span className="text-sm ml-1">
                        ({((getServiceMargin() / parseFloat(ApiReducer?.apiJson?.price)) * 100).toFixed(0)}%)
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Salon parent not found</div>
        </div>
      )}

      <div className='flex mt-5 justify-end'>
        <AppButton
          buttontext={isLoading ? "Processing..." : (editMode ? "Update Service" : "Add Service")}
          onClick={handleSubmit}
          disabled={isLoading}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}

export default AddSalonChild
